import { ref, get, set, push, update, remove } from 'firebase/database';
import { db } from '../config/firebase';
import { Trip, TripMeta, TripMember, TripLodging } from '../types/trip';
import { Settlement } from '../types/expense';
import { generateShareCode } from './sharing';

export async function createTrip(
  creatorUid: string,
  tripData: Partial<TripMeta>,
  creatorMember: Omit<TripMember, 'joinedAt'>
): Promise<string> {
  const tripRef = push(ref(db, 'trips'));
  const tripId = tripRef.key!;
  const shareCode = generateShareCode();

  const meta: TripMeta = {
    tripId,
    tripName: tripData.tripName || 'My Trip',
    destination: tripData.destination || '',
    ...(tripData.occasion ? { occasion: tripData.occasion } : {}),
    startDate: tripData.startDate || '',
    endDate: tripData.endDate || '',
    phase: 'build',
    isPaid: false,
    shareCode,
    createdAt: Date.now(),
    createdBy: creatorUid,
    skinId: 'default',
    designId: 'parlor-screen',
  };

  const member: TripMember = {
    ...creatorMember,
    joinedAt: Date.now(),
  };

  // Atomic multi-path update: meta + member must land together so
  // the security rule (member must exist) is satisfied in a single write.
  // shareCodes is also included since its rules block individual client writes.
  await update(ref(db), {
    [`trips/${tripId}/meta`]: meta,
    [`trips/${tripId}/members/${creatorUid}`]: member,
    [`shareCodes/${shareCode}`]: { tripId, createdAt: Date.now() },
    [`users/${creatorUid}/tripIds/${tripId}`]: true,
  });

  return tripId;
}

export async function getTrip(tripId: string): Promise<Trip | null> {
  const snapshot = await get(ref(db, `trips/${tripId}`));
  return snapshot.exists() ? snapshot.val() : null;
}

export async function updateTripMeta(
  tripId: string,
  updates: Partial<TripMeta>
): Promise<void> {
  await update(ref(db, `trips/${tripId}/meta`), updates);
}

/* ─── Lodging ─── */

export async function saveLodging(tripId: string, lodging: Partial<TripLodging>): Promise<void> {
  await set(ref(db, `trips/${tripId}/lodging`), lodging);
}

export async function deleteLodging(tripId: string): Promise<void> {
  await remove(ref(db, `trips/${tripId}/lodging`));
}

/* ─── Members ─── */

export async function addTripMember(tripId: string, uid: string, member: TripMember): Promise<void> {
  await set(ref(db, `trips/${tripId}/members/${uid}`), member);
}

export async function setTripMembers(tripId: string, members: Record<string, TripMember>): Promise<void> {
  await set(ref(db, `trips/${tripId}/members`), members);
}

export async function getMember(tripId: string, uid: string): Promise<TripMember | null> {
  const snapshot = await get(ref(db, `trips/${tripId}/members/${uid}`));
  return snapshot.exists() ? snapshot.val() : null;
}

/* ─── Crew Identity Claims ─── */

/**
 * Claim an existing crew member placeholder by writing the anonymous user's UID
 * to the claimedBy field. Also writes a claimIndex entry so Firebase security
 * rules can authorize subsequent writes (rules can't scan members for claimedBy,
 * so the index provides a direct lookup at claimIndex/{anonymousUid}).
 *
 * No shadow member is created — the placeholder IS the member record.
 */
export async function claimCrewMember(
  tripId: string,
  crewMemberUid: string,
  anonymousUid: string,
): Promise<void> {
  await update(ref(db), {
    [`trips/${tripId}/members/${crewMemberUid}/claimedBy`]: anonymousUid,
    [`trips/${tripId}/claimIndex/${anonymousUid}`]: true,
  });
}

/**
 * Add a brand-new crew member (for "My name isn't here" flow).
 * Uses the anonymous UID as the member key so they're immediately linked.
 * Also writes claimIndex for Firebase rule authorization.
 */
export async function addNewCrewMemberWithClaim(
  tripId: string,
  anonymousUid: string,
  displayName: string,
): Promise<void> {
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const member: TripMember = {
    uid: anonymousUid,
    displayName,
    initials,
    role: 'crew',
    isAnonymous: false,
    joinedAt: Date.now(),
    claimedBy: anonymousUid,
  };

  // Atomic: create the member AND the claimIndex entry together.
  // The member self-join rule allows writing to members/{auth.uid} in shared phase.
  // The claimIndex rule allows writing to claimIndex/{auth.uid} in shared phase.
  await update(ref(db), {
    [`trips/${tripId}/members/${anonymousUid}`]: member,
    [`trips/${tripId}/claimIndex/${anonymousUid}`]: true,
  });
}

/**
 * Check if an anonymous UID has already claimed a crew member in this trip.
 */
export async function getClaimedMember(
  tripId: string,
  anonymousUid: string,
): Promise<{ memberUid: string; member: TripMember } | null> {
  const snapshot = await get(ref(db, `trips/${tripId}/members`));
  if (!snapshot.exists()) return null;

  const members = snapshot.val() as Record<string, TripMember>;
  for (const [uid, member] of Object.entries(members)) {
    if (member.claimedBy === anonymousUid) {
      return { memberUid: uid, member };
    }
  }
  return null;
}

/* ─── Settlements ─── */

export async function saveSettlement(tripId: string, settlementId: string, data: Settlement & { settledAt?: number }): Promise<void> {
  await update(ref(db, `trips/${tripId}/settlements/${settlementId}`), data);
}

/* ─── Share Codes ─── */

export async function isShareCodeTaken(code: string): Promise<boolean> {
  const snapshot = await get(ref(db, `shareCodes/${code}`));
  return snapshot.exists();
}

export async function saveCustomSlug(tripId: string, newSlug: string, oldSlug: string, originalShareCode: string): Promise<void> {
  // Remove old custom slug (not the original share code)
  if (oldSlug !== originalShareCode) {
    await remove(ref(db, `shareCodes/${oldSlug}`));
  }
  await set(ref(db, `shareCodes/${newSlug}`), { tripId });
  await update(ref(db, `trips/${tripId}/meta`), { customSlug: newSlug });
}

export async function deleteTrip(tripId: string, uid: string): Promise<void> {
  await update(ref(db), {
    [`trips/${tripId}/meta/deleted`]: true,
    [`trips/${tripId}/meta/deletedAt`]: Date.now(),
    [`users/${uid}/tripIds/${tripId}`]: null,
  });
}
